import { z } from 'zod'

/** Calendar meetings default to the session user; other users require calendar access. */
export const meetingListQuerySchema = z.object({
  userId: z.string().uuid().optional(),
  from: z.string().optional(),
  to: z.string().optional(),
})

export {
  PROJECT_PAGE_BLOCK_TYPES,
  type ProjectPageBlock,
  type ProjectPageCreateInput,
  type ProjectPagePatchInput,
  projectPageBlockSchema,
  projectPageBlocksSchema,
  projectPageCreateSchema,
  projectPagePatchSchema,
} from './projectPage'
export {
  type TimeEntry,
  type TimeEntryInput,
  type TimeEntryPatch,
  timeEntryInputSchema,
  timeEntryPatchSchema,
  timeEntrySchema,
} from './timeEntry'
export { loginSchema, type User, type UserInput, userInputSchema, userSchema } from './user'

/** Mail list chips (#778): narrows a folder view or search hits by metadata. */
export const mailListFilterSchema = z.enum(['all', 'unread', 'flagged', 'attachment'])
export type MailListFilter = z.infer<typeof mailListFilterSchema>

export const mailConversationsQuerySchema = z.object({
  folder: z.string().min(1).max(1024).default('INBOX'),
  limit: z.coerce.number().int().min(1).max(200).default(50),
  refresh: z.enum(['0', '1']).optional(),
  filter: mailListFilterSchema.default('all'),
})

/** GET /api/mail/unified/conversations (#535): INBOX of every own account in
 * one list. Same limit and chip semantics as a folder view; no refresh (the
 * client syncs per account). `keep` holds the open mail's thread under a chip. */
export const mailUnifiedQuerySchema = z.object({
  limit: z.coerce.number().int().min(1).max(200).default(50),
  filter: mailListFilterSchema.default('all'),
  keep: z.string().uuid().optional(),
})

/** POST /api/mail/accounts/:id/send (#535) and POST /api/mail/drafts (#830):
 * the internal id of the mail a reply or forward is based on, and on send the
 * saved draft it comes from (its stored source is checked too). Only these
 * fields are validated here; the rest of the body keeps its lenient handling. */
export const mailSendSourceSchema = z.object({
  autoAttachSignature: z.boolean().optional(),
  sourceMessageId: z.string().uuid().optional(),
  draftId: z.string().uuid().optional(),
  /** Sender identity of the sending account (#839); without it the account's
   * default identity, or the account address when it has none. */
  identityId: z.string().uuid().optional(),
})

/** PATCH /api/mail/accounts/:id/push — per-account new-mail push switch and
 * what the push shows (#774). At least one field; `content` is refused for
 * protected accounts by the route. */
export const mailPushPatchSchema = z
  .object({
    enabled: z.boolean().optional(),
    content: z.enum(['count', 'sender', 'sender_subject']).optional(),
  })
  .strict()
  .refine((patch) => patch.enabled !== undefined || patch.content !== undefined, {
    message: 'enabled oder content angeben',
  })

export type MailPushContent = NonNullable<z.infer<typeof mailPushPatchSchema>['content']>

/** GET /api/mail/accounts/:id/address-suggestions (#777). Lenient on purpose:
 * autocomplete must never fail on a bad `limit` or a repeated `q`; a missing,
 * repeated or overlong query becomes '' and yields an empty list. */
export const mailAddressSuggestionsQuerySchema = z.object({
  q: z.string().max(200).catch(''),
  limit: z.unknown().transform((value) => {
    const n = Math.trunc(Number(value))
    return Number.isFinite(n) && n >= 1 ? Math.min(20, n) : 8
  }),
})

export type MailAddressSuggestion = {
  address: string
  name: string | null
  count: number
  lastUsedAt: string
}

/** Mail snooze „Später“ (#780): the IMAP folder that holds snoozed mail. Found
 * by this name (any namespace prefix) or created at the top level. */
export const MAIL_SNOOZE_FOLDER_NAME = 'Später'
/** One conversation per snooze; a request carries at most this many rows. */
export const MAIL_SNOOZE_MAX_MESSAGES = 50
/** How far ahead a snooze may end. The route checks "future" and this bound
 * against the server clock; the web applies the same rule before sending. */
export const MAIL_SNOOZE_MAX_AHEAD_MS = 365 * 24 * 60 * 60 * 1000

/** POST /api/mail/accounts/:id/snoozes — internal mail_messages ids of one
 * conversation and the ISO moment it returns to its folder. */
export const mailSnoozeCreateSchema = z
  .object({
    messageIds: z.array(z.string().uuid()).min(1).max(MAIL_SNOOZE_MAX_MESSAGES),
    until: z.string().datetime({ offset: true }),
  })
  .strict()
export type MailSnoozeCreateInput = z.infer<typeof mailSnoozeCreateSchema>

/** GET /api/mail/snoozes — pending groups of the caller: ids, times and the
 * account only, never subject, sender or any other mail content. */
export type MailSnoozeGroup = {
  groupId: string
  accountId: string
  dueAt: string
  createdAt: string
  count: number
}

/** The primary invoice download and a mail attachment use one format default. */
export const PRIMARY_INVOICE_PDF_FORMAT = 'pdf' as const
export const invoicePdfFormatSchema = z.enum(['pdf', 'zugferd'])
export type InvoicePdfFormat = z.infer<typeof invoicePdfFormatSchema>

export type InvoiceMailBlockedCode =
  | 'INVOICE_MAIL_EXTERNAL_SMTP'
  | 'INVOICE_MAIL_NO_COMPANY'
  | 'INVOICE_MAIL_NO_JOURNAL'
  | 'INVOICE_MAIL_ACCOUNT_DISABLED'

export type InvoiceMailSender = {
  identityId: string
  address: string
  displayName: string | null
  companyId: string | null
  companyName: string | null
  /** null = usable for invoices; otherwise the server refuses it with this code. */
  blockedCode: InvoiceMailBlockedCode | null
}

export type InvoiceMailTemplateOption = {
  id: string
  title: string
  companyIds: string[]
  autoAttachSignature: boolean
}

export type InvoiceMailState = {
  enabled: boolean
  blockedCode: 'INVOICE_MAIL_WARMUP' | null
  recipient: string | null
  customerName: string | null
  pdf: { format: InvoicePdfFormat; name: string; size: number }
  senders: InvoiceMailSender[]
  templates: InvoiceMailTemplateOption[]
  defaultIdentityId: string | null
}

// biome-ignore lint/suspicious/noControlCharactersInRegex: control characters are what we refuse
const LINE_CONTROL_CHARS = /[\u0000-\u001f\u007f]/
// biome-ignore lint/suspicious/noControlCharactersInRegex: body keeps only tab, LF and CR
const BODY_CONTROL_CHARS = /[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/

/** Printable text without control characters (header injection). */
const invoiceMailLine = (max: number) =>
  z
    .string()
    .max(max)
    .refine((value) => !LINE_CONTROL_CHARS.test(value), 'Steuerzeichen nicht erlaubt')

/** Message body: line breaks and tabs only. */
const invoiceMailText = z
  .string()
  .max(20000)
  .refine((value) => !BODY_CONTROL_CHARS.test(value), 'Steuerzeichen nicht erlaubt')

/** Single mailbox, no display name, no list: the sheet sends to exactly one address. */
export const INVOICE_MAIL_RECIPIENT_PATTERN =
  /^[A-Za-z0-9.!#$%&'*+/=?^_`{|}~-]{1,64}@[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?(?:\.[A-Za-z0-9](?:[A-Za-z0-9-]{0,61}[A-Za-z0-9])?)+$/

export const invoiceMailPreviewInputSchema = z
  .object({
    identityId: z.string().uuid(),
    templateId: z.string().uuid().nullable().default(null),
    /** true: subject/text come from the template (placeholders filled server side). */
    applyTemplate: z.boolean().default(false),
    recipient: invoiceMailLine(254).optional(),
    subject: invoiceMailLine(300).optional(),
    text: invoiceMailText.optional(),
    format: invoicePdfFormatSchema.default(PRIMARY_INVOICE_PDF_FORMAT),
  })
  .strict()
export type InvoiceMailPreviewInput = z.input<typeof invoiceMailPreviewInputSchema>

export const invoiceMailSendInputSchema = invoiceMailPreviewInputSchema
  .omit({ applyTemplate: true })
  .extend({
    attemptId: z.string().uuid(),
    confirmation: z.string().regex(/^[a-f0-9]{64}$/),
    recipient: invoiceMailLine(254),
    subject: invoiceMailLine(300),
    text: invoiceMailText,
    /** Must be true when the invoice already has a dispatch record. */
    resend: z.boolean().default(false),
  })
  .strict()
export type InvoiceMailSendInput = z.input<typeof invoiceMailSendInputSchema>

export type InvoiceMailPreview = {
  identityId: string
  fromAddress: string
  fromName: string | null
  companyName: string
  templateId: string | null
  recipient: string
  subject: string
  text: string
  signature: { text: string; attached: boolean; warning: boolean; warningLabel: string | null }
  /** Exactly the text part that is sent: message plus signature (once). */
  finalText: string
  pdf: { format: InvoicePdfFormat; name: string; size: number; sha256: string }
  /** null while the recipient is missing; otherwise binds the send request. */
  confirmation: string | null
  previousSends: number
}

export type InvoiceMailLogEntry = {
  attemptId: string
  actorUserId: string
  status: 'pending' | 'sent' | 'failed' | 'unknown'
  reservedAt: string
  acceptedAt: string | null
  fromAddress: string
  companyName: string
  recipient: string
  messageId: string | null
  pdfFormat: InvoicePdfFormat
  pdfName: string
  pdfSize: number
  pdfSha256: string
  archiveStatus: 'pending' | 'verified' | 'failed'
  archiveProofId: string | null
  archiveVerifiedAt: string | null
  errorCode: string | null
}

export type InvoiceMailSendResult = {
  entry: InvoiceMailLogEntry
  /** The same attempt was already recorded; nothing was sent again. */
  duplicate?: boolean
  /** SMTP accepted the mail but the log update failed; never resend blindly. */
  logSettled?: false
}

/** Optional for legacy callers; each UI creation attempt sends a stable UUID. */
export const entityCreationKeySchema = z.string().uuid().optional()

// #1079: omitted associations stay unchanged; null/[] explicitly clear them.
export const entitySaveOptionsSchema = z.object({
  firmaId: z.string().uuid().nullable().optional(),
  documentIds: z.array(z.string().uuid()).max(1000).optional(),
  baseVersion: z.number().int().positive().optional(),
})
export type EntitySaveOptions = z.infer<typeof entitySaveOptionsSchema>
export const entitySaveRevisionSchema = z.coerce.number().int().min(0).max(2147483647).optional()

// #1336 Cockpit E1: every dossier requires exactly one business area.
export const cockpitIdSchema = z
  .string()
  .uuid()
  .transform((id) => id.toLowerCase())
export const cockpitBereichArtSchema = z.enum(['ki-coaching', 'agentur', 'it', 'gmbh', 'bestand'])
export const cockpitFilterSchema = z
  .object({
    bereich: cockpitIdSchema.optional(),
    firma: cockpitIdSchema.optional(),
  })
  .strict()
export const cockpitKontakteQuerySchema = cockpitFilterSchema
  .extend({
    suche: z.string().trim().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(100).default(50),
    offset: z.coerce.number().int().min(0).max(100000).default(0),
  })
  .strict()
export const cockpitAkteQuerySchema = z.object({ bereich: cockpitIdSchema }).strict()
export const cockpitKontaktParamsSchema = z.object({ id: cockpitIdSchema }).strict()
export const cockpitHeuteQuerySchema = cockpitFilterSchema
  .extend({
    zeitraum: z.enum(['heute', 'woche']).default('heute'),
  })
  .strict()
export const cockpitAdminCommandSchema = z.discriminatedUnion('action', [
  z
    .object({
      action: z.literal('issue'),
      adminUserId: cockpitIdSchema,
      name: z.string().trim().min(1).max(100),
      bereichIds: z.array(cockpitIdSchema).min(1).max(100),
      expiresAt: z
        .string()
        .datetime()
        .refine((s) => Date.parse(s) > Date.now()),
    })
    .strict(),
  z.object({ action: z.literal('revoke'), id: cockpitIdSchema }).strict(),
  z.object({ action: z.literal('list') }).strict(),
])

// #1347: public coaching boundaries; no control characters in mail/calendar fields.
const coachingText = (max: number) =>
  z
    .string()
    .trim()
    .max(max)
    .refine((v) => !/\p{C}/u.test(v), 'Ungültige Zeichen')
const coachingSlug = z.string().regex(/^[a-z0-9-]{1,40}$/)
export const coachingBookingSchema = z
  .object({
    requestId: z.string().uuid(),
    start: z.string().datetime(),
    name: coachingText(120).refine((v) => v.length > 0),
    email: z
      .string()
      .trim()
      .email()
      .max(254)
      .transform((v) => v.toLowerCase()),
    company: coachingText(160).optional().default(''),
    kind: z.enum(['unternehmen', 'privat']),
    topic: coachingText(1000).optional().default(''),
    website: z.literal('').optional().default(''),
    // #1347 Stufe 2: chosen offer and consent evidence for paid offers.
    angebot: coachingSlug.optional().default('kennenlernen'),
    agb: z.boolean().optional().default(false),
    vorzeitig: z.boolean().optional().default(false),
  })
  .strict()
export const coachingCheckoutCancelSchema = z
  .object({
    b: z.string().uuid(),
    t: z.string().regex(/^[A-Za-z0-9_-]{43}$/),
  })
  .strict()
export const coachingOfferSchema = z
  .object({
    slug: coachingSlug,
    titel: coachingText(80).refine((v) => v.length > 0),
    beschreibung: coachingText(300).optional().default(''),
    dauerMin: z.union([z.literal(30), z.literal(60), z.literal(90), z.literal(120)]),
    // Final (gross) price in EUR cents as charged; 0 = free.
    preisCent: z.number().int().min(0).max(100000),
    paketEinheiten: z.number().int().min(1).max(20).default(1),
    aktiv: z.boolean().optional().default(true),
  })
  .strict()
  .refine((o) => o.paketEinheiten === 1 || o.preisCent > 0, 'Pakete brauchen einen Gesamtpreis')
export const coachingManageSchema = z.discriminatedUnion('action', [
  z
    .object({
      action: z.literal('einloesen'),
      requestId: z.string().uuid(),
      start: z.string().datetime(),
    })
    .strict(),
  z.object({ action: z.literal('absagen') }).strict(),
  z.object({ action: z.literal('verschieben'), start: z.string().datetime() }).strict(),
])
export const coachingLinkSchema = z
  .object({
    email: z
      .string()
      .trim()
      .email()
      .max(254)
      .transform((v) => v.toLowerCase()),
    website: z.literal('').optional().default(''),
  })
  .strict()
export const coachingConfigSchema = z
  .object({
    areaId: z.string().uuid(),
    userId: z.string().uuid(),
    identityId: z.string().uuid().nullable(),
    timezone: z.literal('Europe/Berlin').default('Europe/Berlin'),
    bufferBefore: z.number().int().min(0).max(120).default(15),
    bufferAfter: z.number().int().min(0).max(120).default(15),
    noticeHours: z.number().int().min(0).max(720).default(24),
    horizonDays: z.number().int().min(1).max(180).default(60),
    changeHours: z.number().int().min(0).max(720).default(24),
    linkGraceDays: z.number().int().min(1).max(90).default(7),
    showUpcoming: z.boolean().default(true),
    windows: z
      .array(
        z
          .object({
            weekday: z.number().int().min(0).max(6),
            from: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
            to: z.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/),
          })
          .strict()
          .refine((w) => w.from < w.to, 'Ende muss nach Beginn liegen'),
      )
      .max(28),
    // Optional: when given, replaces the area's offers (max 10, unique slugs).
    offers: z
      .array(coachingOfferSchema)
      .max(10)
      .refine((o) => new Set(o.map((x) => x.slug)).size === o.length, 'Kennung doppelt')
      .optional(),
  })
  .strict()
